/**
 * Checkbox filter options with simple label search (no Fuse.js / apiWorker).
 */

import { CheckboxList } from "@risalabs_frontend_org/oasis-ui-kit";
import { useEffect, useMemo, useState } from "react";
import SearchBar from "../searchBar/searchBar";

interface FilterOptionsProps {
  id: string;
  label: string;
  options: { label: string; value: string }[];
  onCheckBoxValueChange: (data: {
    id: string;
    values: string[];
    valuesLabel: string[];
    type: "string" | "number" | "date";
  }) => void;
  defaultValues?: string[];
  type?: "string" | "number" | "date";
}

/**
 * Searchable checkbox list for a single filter section.
 *
 * @param props - {@link FilterOptionsProps}
 * @returns Filter options panel
 */
const FilterOptions = (props: FilterOptionsProps) => {
  const [searchedText, setSearchedText] = useState("");
  const [visibleData, setVisibleData] = useState<
    { label: string; value: string }[]
  >([]);
  const [checkedValues, setCheckedValues] = useState<{
    [key: string]: boolean;
  }>({});
  const [resetSearchBar] = useState(false);

  useEffect(() => {
    const initialCheckedValues = Object.fromEntries(
      props.options.map((item) => [
        item.value,
        props.defaultValues?.includes(item.value) ?? false,
      ]),
    );

    const valuesWithOnlyTrue = Object.keys(initialCheckedValues).filter(
      (key) => initialCheckedValues[key] === true,
    );

    setCheckedValues(
      Object.fromEntries(valuesWithOnlyTrue.map((key) => [key, true])),
    );
  }, [props.id, props.options, props.defaultValues]);

  useEffect(() => {
    if (props.options && props.options.length > 0) {
      setVisibleData(props.options);
    }
  }, [props.options]);

  const optionMap = useMemo(
    () =>
      Object.fromEntries(props.options.map((opt) => [opt.value, opt.label])),
    [props.options],
  );

  const handleSelectionChange = (
    items: { id: string; label: string; checked: boolean }[],
  ) => {
    const updatedCheckedValues = {
      ...checkedValues,
      ...Object.fromEntries(items.map((item) => [item.id, item.checked])),
    };

    const checkedKeys = Object.keys(updatedCheckedValues).filter(
      (key) => updatedCheckedValues[key],
    );

    const selectedLabels = checkedKeys.map(
      (value) => optionMap[value] || value,
    );

    props.onCheckBoxValueChange({
      id: props.id,
      values: checkedKeys,
      valuesLabel: selectedLabels,
      type: props.type ?? "string",
    });
    setCheckedValues(updatedCheckedValues);
  };

  const handleSelectAllClicked = () => {
    const updatedCheckedValues = Object.fromEntries(
      visibleData.map((item) => [item.value, true]),
    );
    const newCheckedValues = { ...checkedValues, ...updatedCheckedValues };

    props.onCheckBoxValueChange({
      id: props.id,
      values: Object.keys(newCheckedValues).filter(
        (key) => newCheckedValues[key],
      ),
      valuesLabel: Object.keys(newCheckedValues)
        .filter((key) => newCheckedValues[key])
        .map((key) => optionMap[key] || key),
      type: props.type ?? "string",
    });
    setCheckedValues(newCheckedValues);
  };

  const handleClearAllClicked = () => {
    const updatedCheckedValuesToBeFalse = Object.fromEntries(
      visibleData.map((item) => [item.value, false]),
    );
    const updatedCheckedValues = {
      ...checkedValues,
      ...updatedCheckedValuesToBeFalse,
    };
    props.onCheckBoxValueChange({
      id: props.id,
      values: Object.keys(updatedCheckedValues).filter(
        (key) => updatedCheckedValues[key] === true,
      ),
      valuesLabel: Object.keys(updatedCheckedValues)
        .filter((key) => updatedCheckedValues[key] === true)
        .map((key) => optionMap[key] || key),
      type: props.type ?? "string",
    });
    setCheckedValues(updatedCheckedValues);
  };

  useEffect(() => {
    const query = searchedText.trim().toLowerCase();
    if (query.length >= 3) {
      setVisibleData(
        props.options.filter((opt) =>
          opt.label.toLowerCase().includes(query),
        ),
      );
    } else {
      setVisibleData(props.options);
    }
  }, [searchedText, props.options]);

  return (
    <div className="filter-options-container flex h-full flex-col gap-2 overflow-hidden px-5">
      <div className="filter-options--header mt-4 text-body font-semibold text-primaryGray-2">
        {props.label}
      </div>
      <div className="filter-options--actions flex justify-end gap-3">
        <div
          className="filter-options--actions__select-all text-small font-semibold text-tertiaryBlue-4 hover:cursor-pointer hover:shadow-sm"
          onClick={handleSelectAllClicked}
        >
          Select All
        </div>
        <div
          className="filter-options--actions__clear-all text-small font-semibold text-tertiaryRed-4 hover:cursor-pointer hover:shadow-sm"
          onClick={handleClearAllClicked}
        >
          Clear Selection
        </div>
      </div>
      <div className="filter-options--body flex flex-1 flex-col overflow-hidden">
        <div className="filter-options--body__search mb-2">
          <SearchBar
            placeHolder={"Search"}
            onSearchClick={(searchedString: string) =>
              setSearchedText(searchedString)
            }
            id={`filter-search-${props.id}`}
            enableSearchOnChange={true}
            onReset={resetSearchBar}
          />
        </div>
        <div className="filter-options--body__options flex-1 overflow-auto">
          <CheckboxList
            items={visibleData.map((item) => ({
              id: item.value,
              label: item.label,
              checked: checkedValues[item.value] === true,
            }))}
            onSelectionChange={handleSelectionChange}
            showSelectAll={false}
            className="custom-checkbox-for-filter-options"
            width={"100%"}
          />
        </div>
      </div>
    </div>
  );
};

export default FilterOptions;
